import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../config/firebaseConfig';
import ImageViewer from '../chat/components/ImageViewer';
import { API_BASE_URL } from '../../config/apiConfig';


const getToken = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;
  return await currentUser.getIdToken();
};

const apiFetch = async (path, options = {}) => {
  const token = await getToken();
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
};

// 액션 한글 레이블
const ACTION_LABELS = {
  register: '회원가입',
  user_signup: '회원가입',
  login: '로그인',
  chat_send: '채팅 전송',
  friend_request: '친구 요청',
  friend_accept: '친구 수락',
  friend_reject: '친구 거절',
  friend_delete: '친구 삭제',
  member_join: '방 입장',
  member_leave: '방 퇴장',
  room_create: '방 생성',
  invite_send: '방 초대',
  schedule_add: '일정 추가',
  schedule_delete: '일정 삭제',
  user_withdraw: '회원 탈퇴',
  room_invite: '방 참여',
};

const ACTION_STYLE = {
  register: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  user_signup: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  login: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  chat_send: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
  friend_request: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  friend_accept: 'bg-green-50 text-green-700 ring-1 ring-green-200',
  friend_reject: 'bg-red-50 text-red-600 ring-1 ring-red-200',
  friend_delete: 'bg-red-50 text-red-600 ring-1 ring-red-200',
  member_join: 'bg-teal-50 text-teal-700 ring-1 ring-teal-200',
  member_leave: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
  room_create: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  invite_send: 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200',
  schedule_add: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
  schedule_delete: 'bg-pink-50 text-pink-700 ring-1 ring-pink-200',
  user_withdraw: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  room_invite: 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200',
};

const ACTION_DOT = {
  register: 'bg-emerald-400',
  user_signup: 'bg-emerald-400',
  login: 'bg-blue-400',
  chat_send: 'bg-slate-400',
  friend_request: 'bg-amber-400',
  friend_accept: 'bg-green-400',
  friend_reject: 'bg-red-400',
  friend_delete: 'bg-red-400',
  member_join: 'bg-teal-400',
  member_leave: 'bg-orange-400',
  room_create: 'bg-violet-400',
  invite_send: 'bg-cyan-400',
  schedule_add: 'bg-indigo-400',
  schedule_delete: 'bg-pink-400',
  user_withdraw: 'bg-red-500',
  room_invite: 'bg-cyan-400',
};

function ActionBadge({ type }) {
  const label = ACTION_LABELS[type] || type;
  const style = ACTION_STYLE[type] || 'bg-gray-100 text-gray-600 ring-1 ring-gray-200';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${style}`}>
      {label}
    </span>
  );
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
}
function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

// ─────────────────────────────────────────────────────────────────────────────
// 탭: 대시보드
// ─────────────────────────────────────────────────────────────────────────────
function TabDashboard() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [sRes, aRes] = await Promise.all([
        apiFetch('/api/admin/stats'),
        apiFetch('/api/admin/activities?limit=8&period=today'),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (aRes.ok) {
        const d = await aRes.json();
        setActivities(d.activities || []);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const cards = [
    { label: '총 회원', value: stats?.totalUsers ?? '-', bg: 'linear-gradient(135deg, #3b82f6, #2563eb)', icon: '👥' },
    { label: '총 방', value: stats?.totalRooms ?? '-', bg: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', icon: '🏠' },
    { label: '활성 회원', value: stats?.activeUsers ?? '-', bg: 'linear-gradient(135deg, #10b981, #059669)', icon: '✅' },
    { label: '관리자', value: stats?.adminUsers ?? '-', bg: 'linear-gradient(135deg, #f97316, #ea580c)', icon: '🔑' },
    { label: '7일 내 가입', value: stats?.recentSignups ?? '-', bg: 'linear-gradient(135deg, #ec4899, #db2777)', icon: '🆕' },
  ];

  return (
    <div className="p-5 space-y-6">
      {/* 스탯 카드 */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map(c => (
          <div key={c.label} style={{ background: c.bg }} className="text-white rounded-2xl p-4 shadow-sm">
            <div className="text-2xl mb-1">{c.icon}</div>
            <div className="text-2xl font-bold leading-none">{c.value}</div>
            <div className="text-xs mt-1.5 opacity-80 font-medium">{c.label}</div>
          </div>
        ))}
      </div>

      {/* 오늘 활동 */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-5 bg-indigo-500 rounded-full" />
          <h3 className="font-semibold text-gray-800">오늘 활동</h3>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {activities.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">오늘 활동 없음</div>
          ) : (
            activities.map((a, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-3 ${i < activities.length - 1 ? 'border-b border-gray-50' : ''}`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${ACTION_DOT[a.type] || 'bg-gray-300'}`} />
                <span className="text-sm font-medium text-gray-800 shrink-0">{a.userName}</span>
                <span className="text-xs text-gray-400 flex-1 truncate">{a.details}</span>
                <ActionBadge type={a.type} />
                <span className="text-xs text-gray-300 shrink-0">{formatTime(a.createdAt)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 탭: 회원 관리
// ─────────────────────────────────────────────────────────────────────────────
function TabUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const loadUsers = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    const res = await apiFetch(`/api/admin/users?page=${p}&limit=15&search=${encodeURIComponent(q)}`);
    if (res.ok) {
      const d = await res.json();
      setUsers(d.users || []);
      setTotal(d.pagination?.total || 0);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(1, search); }, []); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadUsers(1, search);
  };

  const handlePromote = async (userId) => {
    if (!window.confirm('관리자로 승급하시겠습니까?')) return;
    setActionLoading(userId + '_promote');
    await apiFetch(`/api/admin/users/${userId}/promote`, { method: 'PUT' });
    setActionLoading(null);
    loadUsers(page, search);
  };

  const handleDemote = async (userId) => {
    if (!window.confirm('일반 사용자로 강등하시겠습니까?')) return;
    setActionLoading(userId + '_demote');
    await apiFetch(`/api/admin/users/${userId}/demote`, { method: 'PUT' });
    setActionLoading(null);
    loadUsers(page, search);
  };

  const handleDelete = async (userId, email) => {
    if (!userId || userId === 'undefined') {
      alert('유효하지 않은 사용자입니다.');
      return;
    }
    if (!window.confirm(`"${email}" 계정을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;
    setActionLoading(userId + '_delete');
    await apiFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
    setActionLoading(null);
    loadUsers(page, search);
  };

  const totalPages = Math.ceil(total / 15);

  return (
    <div className="p-5 space-y-4">
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="이름, 이메일 검색..."
          className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
        />
        <button type="submit" className="px-5 py-2.5 bg-indigo-500 text-white rounded-xl text-sm font-medium hover:bg-indigo-600 transition-colors shadow-sm">
          검색
        </button>
      </form>

      <div className="text-xs text-gray-400 font-medium">총 {total}명</div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-6 h-6 border-3 border-indigo-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.id} className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow">
              {/* 아바타 */}
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white font-bold text-sm shrink-0">
                {(u.firstName || u.name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-800 truncate">
                    {u.firstName || u.name}
                  </span>
                  {u.role === 'admin' && (
                    <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-medium">관리자</span>
                  )}
                </div>
                <div className="text-xs text-gray-400 truncate mt-0.5">{u.email}</div>
                <div className="text-xs text-gray-300 mt-0.5">
                  가입 {new Date(u.createdAt).toLocaleDateString('ko-KR')}
                  {u.lastLoginAt && ` · 접속 ${new Date(u.lastLoginAt).toLocaleDateString('ko-KR')}`}
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {u.role !== 'admin' ? (
                  <button
                    onClick={() => handlePromote(u.id)}
                    disabled={!!actionLoading}
                    className="text-xs px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors font-medium border border-orange-200"
                  >
                    관리자↑
                  </button>
                ) : (
                  <button
                    onClick={() => handleDemote(u.id)}
                    disabled={!!actionLoading}
                    className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    강등↓
                  </button>
                )}
                <button
                  onClick={() => handleDelete(u.id, u.email)}
                  disabled={!!actionLoading}
                  className="text-xs px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium border border-red-200"
                >
                  삭제
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 pt-2">
          <button
            disabled={page <= 1}
            onClick={() => { const p = page - 1; setPage(p); loadUsers(p, search); }}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-30 transition-colors"
          >← 이전</button>
          <span className="px-4 py-2 text-sm text-gray-500">{page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => { const p = page + 1; setPage(p); loadUsers(p, search); }}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-30 transition-colors"
          >다음 →</button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 탭: 방 관리
// ─────────────────────────────────────────────────────────────────────────────
function TabRooms() {
  const [rooms, setRooms] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [viewerState, setViewerState] = useState(null);

  const loadRooms = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    const res = await apiFetch(`/api/admin/rooms?page=${p}&limit=15&search=${encodeURIComponent(q)}`);
    if (res.ok) {
      const d = await res.json();
      setRooms(d.rooms || []);
      setTotal(d.pagination?.total || 0);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadRooms(1, ''); }, []); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadRooms(1, search);
  };

  const handleDeleteRoom = async (roomId, name) => {
    if (!window.confirm(`"${name}" 방을 삭제하시겠습니까?`)) return;
    await apiFetch(`/api/admin/rooms/${roomId}`, { method: 'DELETE' });
    loadRooms(page, search);
  };

  const handleViewLogs = async (room) => {
    setSelectedRoom(room);
    setLogsLoading(true);
    const res = await apiFetch(`/api/admin/rooms/${room._id}/logs?limit=100`);
    if (res.ok) {
      const d = await res.json();
      setLogs(d.logs || []);
    }
    setLogsLoading(false);
  };

  const totalPages = Math.ceil(total / 15);

  // 방 로그 뷰
  if (selectedRoom) {
    const grouped = logs.reduce((acc, log) => {
      const key = new Date(log.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
      if (!acc[key]) acc[key] = [];
      acc[key].push(log);
      return acc;
    }, {});
    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));

    return (
      <>
      <div className="p-5 space-y-4">
        {/* 헤더 */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedRoom(null)}
            className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors"
          >
            ← 목록
          </button>
          <div>
            <h3 className="font-bold text-gray-800">"{selectedRoom.name}"</h3>
            <p className="text-xs text-gray-400">{logs.length}개의 로그</p>
          </div>
        </div>

        {logsLoading ? (
          <div className="flex justify-center py-10">
            <div className="w-6 h-6 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-4xl mb-3">📭</div>
            <p className="text-sm">로그가 없습니다</p>
          </div>
        ) : (
          <div className="space-y-5">
            {sortedDates.map(date => (
              <div key={date}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-xs font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{date}</div>
                  <div className="flex-1 h-px bg-gray-100" />
                </div>
                <div className="relative pl-5 space-y-2">
                  <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-100" />
                  {grouped[date].map((log, i) => (
                    <div key={i} className="relative">
                      <div className={`absolute -left-3.5 top-2.5 w-2 h-2 rounded-full ${ACTION_DOT[log.action] || 'bg-gray-300'}`} />
                      <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm text-gray-800">{log.userName || '알 수 없음'}</span>
                          <ActionBadge type={log.action} />
                          <span className="ml-auto text-xs text-gray-300">{formatTime(log.createdAt)}</span>
                        </div>
                        {log.fileType?.startsWith('image/') && log.fileUrl ? (
                          <img
                            src={log.fileUrl.startsWith('http') ? log.fileUrl : `${API_BASE_URL}${log.fileUrl}`}
                            alt={log.fileName || '이미지'}
                            className="mt-1 rounded-lg cursor-pointer object-cover"
                            style={{ width: 120, height: 120 }}
                            onClick={() => {
                              const imgs = logs.filter(l => l.fileType?.startsWith('image/') && l.fileUrl);
                              const idx = imgs.findIndex(l => l._id === log._id);
                              setViewerState({
                                images: imgs.map(l => ({
                                  src: l.fileUrl.startsWith('http') ? l.fileUrl : `${API_BASE_URL}${l.fileUrl}`,
                                  senderName: l.userName,
                                  createdAt: l.createdAt,
                                  messageId: l._id,
                                  fileName: l.fileName || '이미지',
                                  isMe: false,
                                })),
                                initialIndex: idx >= 0 ? idx : 0,
                              });
                            }}
                          />
                        ) : log.details ? (
                          <p className="text-xs text-gray-500 leading-relaxed">{log.details}</p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewerState && (
        <ImageViewer
          images={viewerState.images}
          initialIndex={viewerState.initialIndex}
          onClose={() => setViewerState(null)}
          onDownload={(src, fileName) => {
            const a = document.createElement('a');
            a.href = src; a.download = fileName || '이미지'; a.click();
          }}
        />
      )}
      </>
    );
  }

  return (
    <div className="p-5 space-y-4">
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="방 이름 검색..."
          className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
        />
        <button type="submit" className="px-5 py-2.5 bg-indigo-500 text-white rounded-xl text-sm font-medium hover:bg-indigo-600 transition-colors shadow-sm">
          검색
        </button>
      </form>

      <div className="text-xs text-gray-400 font-medium">총 {total}개</div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-6 h-6 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {rooms.map(r => (
            <div key={r._id} className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-white text-lg shrink-0">
                🏠
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-800 truncate">{r.name}</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  방장 {r.owner ? `${r.owner.firstName || r.owner.name || ''}` : '알 수 없음'}
                  <span className="mx-1.5">·</span>
                  멤버 {r.memberCount}명
                  <span className="mx-1.5">·</span>
                  {new Date(r.createdAt).toLocaleDateString('ko-KR')}
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  onClick={() => handleViewLogs(r)}
                  className="text-xs px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors font-medium border border-indigo-200"
                >
                  로그 보기
                </button>
                <button
                  onClick={() => handleDeleteRoom(r._id, r.name)}
                  className="text-xs px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium border border-red-200"
                >
                  삭제
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 pt-2">
          <button
            disabled={page <= 1}
            onClick={() => { const p = page - 1; setPage(p); loadRooms(p, search); }}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-30 transition-colors"
          >← 이전</button>
          <span className="px-4 py-2 text-sm text-gray-500">{page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => { const p = page + 1; setPage(p); loadRooms(p, search); }}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-30 transition-colors"
          >다음 →</button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 탭: 활동 로그
// ─────────────────────────────────────────────────────────────────────────────
const PERIODS = [
  { key: 'today', label: '오늘' },
  { key: 'week', label: '이번 주' },
  { key: 'month', label: '이번 달' },
  { key: '', label: '전체' },
];

function TabLogs() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');

  const load = useCallback(async (p) => {
    setLoading(true);
    const query = p ? `?limit=100&period=${p}` : '?limit=100';
    const res = await apiFetch(`/api/admin/activities${query}`);
    if (res.ok) {
      const d = await res.json();
      setActivities(d.activities || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  // 날짜별 그룹핑
  const grouped = activities.reduce((acc, a) => {
    const key = new Date(a.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));

  return (
    <div className="p-5 space-y-4">
      {/* 기간 필터 */}
      <div className="flex gap-2">
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              period === p.key
                ? 'bg-indigo-500 text-white shadow-sm'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-6 h-6 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">📭</div>
          <p className="text-sm">해당 기간에 활동이 없습니다</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDates.map(date => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-xs font-bold text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">{date}</div>
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-300">{grouped[date].length}건</span>
              </div>
              <div className="relative pl-5 space-y-2">
                <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-100" />
                {grouped[date].map((a, i) => (
                  <div key={i} className="relative">
                    <div className={`absolute -left-3.5 top-3 w-2 h-2 rounded-full ${ACTION_DOT[a.type] || 'bg-gray-300'}`} />
                    <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 shadow-sm flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-gray-800">{a.userName}</span>
                          <ActionBadge type={a.type} />
                        </div>
                        {a.details && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{a.details}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-300 shrink-0">{formatTime(a.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 탭: 실시간 접속
// ─────────────────────────────────────────────────────────────────────────────
function TabOnline() {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    const res = await apiFetch('/api/admin/online');
    if (res.ok) {
      const d = await res.json();
      setOnlineUsers(d.users || []);
      setLastUpdated(new Date());
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  return (
    <div className="p-5 space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-sm font-semibold text-gray-700">
            현재 접속 중 <span className="text-green-600">{onlineUsers.length}명</span>
          </span>
        </div>
        <button
          onClick={load}
          className="text-xs text-indigo-500 hover:text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg font-medium transition-colors"
        >
          새로고침
        </button>
      </div>

      {lastUpdated && (
        <p className="text-xs text-gray-300">
          마지막 업데이트: {lastUpdated.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          <span className="ml-2">(10초마다 자동 갱신)</span>
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-6 h-6 border-4 border-green-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : onlineUsers.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">💤</div>
          <p className="text-sm text-gray-400 font-medium">현재 접속 중인 사용자가 없습니다</p>
          <p className="text-xs text-gray-300 mt-1">앱에서 로그인하면 여기 표시됩니다</p>
        </div>
      ) : (
        <div className="space-y-2">
          {onlineUsers.map((u, i) => {
            const mins = u.connectedAt
              ? Math.floor((Date.now() - new Date(u.connectedAt)) / 60000)
              : null;
            const timeLabel = mins === null
              ? ''
              : mins < 1 ? '방금 접속'
              : mins < 60 ? `${mins}분 전 접속`
              : `${Math.floor(mins / 60)}시간 전 접속`;
            return (
              <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
                <div className="relative">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">
                    {(u.name || u.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-400 border-2 border-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-800 truncate">{u.name || '이름 없음'}</div>
                  <div className="text-xs text-gray-400 truncate">{u.email}</div>
                </div>
                <div className="text-xs text-green-500 font-medium shrink-0">{timeLabel}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 메인 AdminDashboard
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'dashboard', label: '대시보드', icon: '📊' },
  { key: 'users', label: '회원', icon: '👥' },
  { key: 'rooms', label: '방', icon: '🏠' },
  { key: 'logs', label: '로그', icon: '📋' },
  { key: 'online', label: '접속', icon: '🟢' },
];

const AdminDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('dashboard');

  const handleLogout = async () => {
    await auth.signOut();
    window.location.href = '/auth';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center text-lg">
            🛡️
          </div>
          <div>
            <div className="font-bold text-base leading-tight">관리자 대시보드</div>
            <div className="text-xs opacity-70">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="text-xs font-medium px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
        >
          로그아웃
        </button>
      </div>

      {/* 탭 내비게이션 */}
      <div className="bg-white border-b border-gray-100 shadow-sm overflow-x-auto">
        <div className="flex min-w-max px-2">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                activeTab === t.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              <span className="text-base">{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 콘텐츠 */}
      <div className="max-w-4xl mx-auto">
        {activeTab === 'dashboard' && <TabDashboard />}
        {activeTab === 'users' && <TabUsers />}
        {activeTab === 'rooms' && <TabRooms />}
        {activeTab === 'logs' && <TabLogs />}
        {activeTab === 'online' && <TabOnline />}
      </div>
    </div>
  );
};

export default AdminDashboard;
